import { Component, ErrorHandler, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';

import { Business } from '../models/business.model';
import { environment } from '../../../environments/environment';
import { storageAvailable } from '../../util/helpers';


@Component({
  selector: 'app-flyingclub-page',
  templateUrl: './flyingclub-page.component.html',
  styleUrls: ['./flyingclub-page.component.scss']
})
export class FlyingclubPageComponent implements OnInit {
  business: Business;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _meta: Meta,
    private _title: Title,
    private _errorHandler: ErrorHandler
  ) { }

  ngOnInit() {
    this._route.data
      .subscribe((data: { business: Business }) => {
        this.business = data.business;
        this._updateMeta(data.business);
      }, (error: any) => {
        this._errorHandler.handleError(error);
      });
  }

  private _updateMeta(business: Business) {
    let title = business.name;
    const airport = this.business.airports[0];
    if (airport) {
      title += ` at ${airport.identifier}`;
    }
    this._title.setTitle(`${title} - ${environment.appName}`);
    this._meta.removeTag('name="description"');
  }

  /**
   * Returns the airport identifier to use for the "Back to [airport]" link in the header. If the user navigated to
   * the business page from an airport page, then the referer airport should be used (stored in sessionStorage).
   * Otherwise, this will use the first associated airport for the business. If the business is not associated with
   * any airport, this will return null.
   * @returns {string|null}
   */
  get airportIdent(): string|null {
    if (!this.business) {
      return null;
    }

    // Get the associated airports (usually, there is only one, but a business may be associated with multiple
    // airports)
    const associatedAirports = this.business.airports || [];

    // Check if there was an airport set in sessionStorage, and that airport is associated with this business.
    if (storageAvailable('sessionStorage')) {
      const lastAirport = sessionStorage.getItem('lastAirport');
      if (lastAirport && associatedAirports.some(airport => airport.identifier === lastAirport)) {
        return lastAirport;
      }
    }

    // Otherwise, return the identifier of the first associated airport (or null if there are no associated airports).
    return associatedAirports.length ? associatedAirports[0].identifier : null;
  }

  /**
   * The Flying Club Finder search results URL to return to, when the user arrived here from a search. It is
   * set by FlyingClubFinderResultsListingComponent.onFlyingClubSelected and cleared whenever an airport page
   * is viewed, so it reflects the freshest navigation source.
   * @returns {string|null}
   */
  get searchReturnUrl(): string|null {
    if (!storageAvailable('sessionStorage')) {
      return null;
    }
    return sessionStorage.getItem('flyingClubSearchReturn');
  }

  get cameFromSearch(): boolean {
    return !!this.searchReturnUrl;
  }

  /**
   * Text for the "Back to ..." link: the originating search when the user came from the Flying Club Finder,
   * otherwise the associated airport (or "Search" when there is none).
   * @returns {string}
   */
  get backLabel(): string {
    if (this.cameFromSearch) {
      return 'Search';
    }
    return this.airportIdent ? this.airportIdent : 'Search';
  }

  /**
   * Navigates back to wherever the user came from: the Flying Club Finder results (preserving the query) when
   * that is the origin, otherwise the associated airport page, falling back to a fresh search.
   */
  goBack() {
    const returnUrl = this.searchReturnUrl;
    if (returnUrl) {
      this._router.navigateByUrl(returnUrl);
      return;
    }
    if (this.airportIdent) {
      this._router.navigate(['/airports', this.airportIdent]);
      return;
    }
    this._router.navigate(['/search']);
  }
}
